import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAgentOpsStatusQuery,
  useGetAgentOpsAgentsQuery,
  useGetAgentOpsBatchesQuery,
  useLaunchAgentOpsBatchMutation,
  useRefreshAgentOpsMutation,
  useGetAgentOpsConfigsQuery,
  useSaveAgentOpsConfigMutation,
  useDeleteAgentOpsConfigMutation,
} from "../../../app/api/console/agentOperationsApiSlice";
import GroupingView from "./GroupingView";
import WebsitePicker from "./WebsitePicker";

const STATUS_STYLES = {
  queued: "bg-surface-neutral text-text-secondary",
  resolving: "bg-amber-100 text-amber-700",
  launching: "bg-amber-100 text-amber-700",
  running: "bg-blue-100 text-blue-700",
  request: "bg-purple-100 text-purple-700",
  done: "bg-green-100 text-green-700",
  settled: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

function StatusPill({ status, label }) {
  const cls = STATUS_STYLES[status] || "bg-surface-neutral text-text-secondary";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label || status}</span>;
}
function short(s) {
  if (!s) return "—";
  return s.length > 18 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
}

const TABS = [
  { id: "launch", label: "Launch" },
  { id: "grouping", label: "Grouping" },
];

const DEFAULT_PROMPT = "Find issues with the Product Info agent on Bestsellers";
const DEFAULT_BUDGET = "10";

export default function AgentOperations() {
  const [tab, setTab] = useState("launch");
  const [toast, setToast] = useState(null);

  const { data: status } = useGetAgentOpsStatusQuery(undefined, { pollingInterval: 20000 });
  const [refreshCost, { isLoading: refreshing }] = useRefreshAgentOpsMutation();

  const onRefresh = async () => {
    setToast({ kind: "", msg: "Refreshing status + cost…" });
    try {
      const r = await refreshCost().unwrap();
      setToast({ kind: "ok", msg: `Refreshed ${r.refreshed} run(s).` });
    } catch (e) {
      setToast({ kind: "bad", msg: e?.data?.detail || "Refresh failed" });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2 text-text-primary">Agent Operations</h1>
          <p className="text-body-sm text-text-secondary mt-1">
            Batch-launch agents on staging websites · saved configs · priced history · grouping
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge label="platform" ok={status?.platform_ok} />
          <Badge label="mcp" ok={status?.mcp_ok} />
          <Badge label="db" ok={status?.db_ok} />
          <span className="px-2.5 py-1 text-[11px] rounded-lg bg-surface-neutral text-text-secondary">
            queue {status?.queue_depth ?? 0} · conc {status?.concurrency ?? "–"}
          </span>
          {tab === "launch" && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="px-3 py-2 text-body-xs font-semibold border border-border-default rounded-xl text-text-secondary hover:bg-surface-neutral disabled:opacity-50"
              title="Refresh status + real cost from the orchestrator">
              ↻ cost/status
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border-default">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-body-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-button-primary text-text-primary" : "border-transparent text-text-tertiary hover:text-text-secondary"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div className={`text-body-sm px-3 py-2 rounded-xl ${toast.kind === "ok" ? "bg-green-50 text-green-700" : toast.kind === "bad" ? "bg-red-50 text-red-700" : "bg-surface-neutral text-text-secondary"}`}>
          {toast.msg}
        </div>
      )}

      {tab === "launch" && <LaunchTab onToast={setToast} />}
      {tab === "grouping" && <GroupingView onToast={setToast} />}
    </div>
  );
}

function LaunchTab({ onToast }) {
  const { data: agentsData } = useGetAgentOpsAgentsQuery();
  const { data: configsData } = useGetAgentOpsConfigsQuery();
  const [launch, { isLoading: launching }] = useLaunchAgentOpsBatchMutation();
  const [saveConfig, { isLoading: saving }] = useSaveAgentOpsConfigMutation();
  const [deleteConfig] = useDeleteAgentOpsConfigMutation();

  // Not memoised on purpose: `agentsData.agents` is already a stable RTK Query reference
  // and the `[]` fallback only applies while agents are loading; wrapping it in useMemo
  // would change its identity semantics for the `selectedAgent` memo below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agents = agentsData?.agents || [];
  const configs = configsData?.configs || [];

  const [configId, setConfigId] = useState("");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [agentSlug, setAgentSlug] = useState("");
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [budget, setBudget] = useState(DEFAULT_BUDGET);

  const urlList = useMemo(() => selected.map((s) => s.url), [selected]);
  const selectedAgent = useMemo(() => agents.find((a) => a.slug === agentSlug), [agents, agentSlug]);
  const agentTitle = selectedAgent?.title || "";
  // runtime_agent_id maps to the MCP `agent_id` (AGENT.md). Most agents have none
  // → orchestrator default, exactly like the platform's /agents/{slug}/run.
  const agentId = selectedAgent?.runtime_agent_id || null;

  // Picking an agent sets the INITIAL prompt (its real launch_prompt, else a
  // template) — the same text the platform would build. Editable afterwards.
  const onAgentChange = (slug) => {
    setAgentSlug(slug);
    const a = agents.find((x) => x.slug === slug);
    if (!a) {
      setPrompt(DEFAULT_PROMPT);
      return;
    }
    const lp = (a.launch_prompt || "").trim();
    setPrompt(lp || `Run the '${a.title || slug}' agent on this site.`);
  };

  // A config holds the reusable settings (agent + prompt + budget), NOT the
  // website URLs — those are entered fresh for each launch.
  const loadConfig = (cid) => {
    setConfigId(cid);
    const c = configs.find((x) => x.id === cid);
    if (!c) {
      setName(""); setAgentSlug(""); setPrompt(DEFAULT_PROMPT); setBudget(DEFAULT_BUDGET);
      return;
    }
    setName(c.name || "");
    setAgentSlug(c.agent_slug || "");
    setPrompt(c.prompt || "");
    setBudget(c.budget_usd != null ? String(c.budget_usd) : "");
  };

  const onSave = async () => {
    if (!name.trim()) return onToast({ kind: "bad", msg: "Give the config a name to save it." });
    try {
      const res = await saveConfig({
        config_id: configId || null,
        name: name.trim(),
        prompt,
        agent_slug: agentSlug || null,
        agent_title: agentTitle || null,
        budget_usd: budget ? Number(budget) : null,
      }).unwrap();
      setConfigId(res.config_id);
      onToast({ kind: "ok", msg: `Config "${name}" saved.` });
    } catch (e) {
      onToast({ kind: "bad", msg: e?.data?.detail || "Save failed" });
    }
  };

  const onDelete = async () => {
    if (!configId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete config "${name}"?`)) return;
    try {
      await deleteConfig(configId).unwrap();
      loadConfig("");
      onToast({ kind: "ok", msg: "Config deleted." });
    } catch (e) {
      onToast({ kind: "bad", msg: e?.data?.detail || "Delete failed" });
    }
  };

  const onLaunch = async () => {
    if (!urlList.length) return onToast({ kind: "bad", msg: "Add at least one URL." });
    if (!prompt.trim()) return onToast({ kind: "bad", msg: "A prompt is required." });
    try {
      const res = await launch({
        name: name.trim() || null,
        urls: urlList,
        prompt,
        agent_slug: agentSlug || null,
        agent_id: agentId || null,
        agent_title: agentTitle || null,
        budget_usd: budget ? Number(budget) : null,
      }).unwrap();
      onToast({ kind: "ok", msg: `Launched ${res.runs_planned} website(s) on staging.` });
    } catch (e) {
      onToast({ kind: "bad", msg: e?.data?.detail || "Launch failed" });
    }
  };

  const label = "block text-body-xs font-semibold text-text-secondary mb-1";
  const input = "w-full px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40";

  return (
    <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
      <div className="bg-white border border-border-default rounded-2xl p-5 space-y-4">
        <h2 className="text-h4 text-text-primary">Launch Batch</h2>
        <p className="text-body-xs text-text-tertiary">
          Paste website URLs (one per line). Missing websites are created on staging (with scope
          selection) the same way as “New Website”. Then the same prompt + budget is launched on
          each website’s orchestrator.
        </p>

        <WebsitePicker selected={selected} onChange={setSelected} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={label}>Agent (optional)</label>
            <select value={agentSlug} onChange={(e) => onAgentChange(e.target.value)} className={input}>
              <option value="">— none (orchestrator) —</option>
              {agents.map((a) => (
                <option key={a.slug} value={a.slug}>{a.title || a.slug} ({a.kind})</option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Budget per website (USD, optional)</label>
            <input type="number" min="0" step="0.5" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 5" className={input} />
          </div>
        </div>

        <div>
          <label className={label}>Prompt (sent to the orchestrator for every website)</label>
          <textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder="Find issues with the Product Info agent on this site." className={input} />
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-body-xs text-text-tertiary">
            {urlList.length ? `${urlList.length} website(s) on staging${budget ? ` · ~$${(Number(budget) * urlList.length).toFixed(0)} max` : ""}` : "—"}
          </span>
          <button onClick={onLaunch} disabled={!urlList.length || !prompt.trim() || launching}
            className="px-4 py-2 text-body-xs font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover disabled:opacity-40 disabled:cursor-not-allowed">
            {launching ? "Launching…" : "Launch Batch"}
          </button>
        </div>
      </div>

      {/* Configs panel */}
      <div className="bg-white border border-border-default rounded-2xl p-5 space-y-3">
        <h2 className="text-h4 text-text-primary">Saved configs</h2>
        <select value={configId} onChange={(e) => loadConfig(e.target.value)} className={input}>
          <option value="">— new / unsaved —</option>
          {configs.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <div>
          <label className={label}>Config name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bestsellers — Product Info" className={input} />
        </div>
        <div className="flex gap-2">
          <button onClick={onSave} disabled={saving}
            className="flex-1 px-3 py-2 text-body-xs font-semibold border border-border-default rounded-xl text-text-secondary hover:bg-surface-neutral disabled:opacity-50">
            {configId ? "Update" : "Save"}
          </button>
          <button onClick={onDelete} disabled={!configId}
            className="px-3 py-2 text-body-xs font-semibold border border-red-200 text-red-600 rounded-xl hover:bg-red-50 disabled:opacity-40">
            Delete
          </button>
        </div>
        <p className="text-[11px] text-text-tertiary">
          A config saves the reusable settings — agent, prompt and budget — not the
          websites. Paste the URLs fresh for each launch.
        </p>
      </div>
    </div>

      <div>
        <h2 className="text-h4 text-text-primary mb-3">History</h2>
        <HistoryTab onToast={onToast} />
      </div>
    </div>
  );
}

function HistoryTab({ onToast }) {
  const { data } = useGetAgentOpsBatchesQuery(undefined, { pollingInterval: 5000 });
  const { data: status } = useGetAgentOpsStatusQuery();
  const [saveConfig] = useSaveAgentOpsConfigMutation();
  const [expanded, setExpanded] = useState(() => new Set());
  const navigate = useNavigate();
  const front = status?.platform_front_url;
  const batches = data?.batches || [];

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveAsConfig = async (b) => {
    const def = b.name || b.agent_title || "Config from launch";
    // eslint-disable-next-line no-alert
    const newName = window.prompt("Save these settings (agent + prompt + budget) as a config named:", def);
    if (!newName || !newName.trim()) return;
    try {
      await saveConfig({
        name: newName.trim(),
        prompt: b.prompt || null,
        agent_slug: b.agent_slug || null,
        agent_title: b.agent_title || null,
        budget_usd: b.budget_usd != null ? b.budget_usd : null,
      }).unwrap();
      onToast?.({ kind: "ok", msg: `Saved config "${newName.trim()}".` });
    } catch (e) {
      onToast?.({ kind: "bad", msg: e?.data?.detail || "Save failed" });
    }
  };

  const thClass = "px-3 py-2 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]";

  if (!batches.length) {
    return <div className="bg-white border border-border-default rounded-2xl p-5 text-body-sm text-text-tertiary">No launches yet.</div>;
  }

  return (
    <div className="space-y-3">
      {batches.map((b) => {
        const s = b.summary || {};
        const open = expanded.has(b.id);
        const runsTxt = Object.entries(s.runs_by_status || {}).map(([k, v]) => `${v} ${k}`).join(" · ") || "no runs";
        return (
          <div key={b.id} className="bg-white border border-border-default rounded-xl overflow-hidden">
            <div onClick={() => toggle(b.id)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-neutral/50 text-left cursor-pointer">
              <div className="min-w-0">
                <div className="text-body-sm font-semibold text-text-primary truncate">
                  {open ? "▾ " : "▸ "}{b.name || b.agent_title || "(prompt launch)"}
                </div>
                <div className="text-caption text-text-tertiary mt-0.5 truncate">
                  {new Date(b.created_at * 1000).toLocaleString()} · {s.sites_total} website(s)
                  {b.agent_title ? ` · ${b.agent_title}` : ""}
                  {b.budget_usd != null ? ` · $${b.budget_usd}/site cap` : ""} · {runsTxt}
                </div>
                {b.prompt && <div className="text-[11px] text-text-tertiary mt-0.5 truncate italic">“{b.prompt}”</div>}
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                <button
                  onClick={(e) => { e.stopPropagation(); saveAsConfig(b); }}
                  className="px-2.5 py-1 text-[11px] font-semibold border border-border-default rounded-lg text-text-secondary hover:bg-white"
                  title="Save this launch's agent + prompt + budget as a reusable config">
                  Save as config
                </button>
                <div className="text-right">
                  <div className="text-body-sm font-bold text-text-primary">${(s.cost_usd || 0).toFixed(2)}</div>
                  <div className="text-[10px] text-text-tertiary uppercase">real cost</div>
                </div>
              </div>
            </div>
            {open && (
              <div className="px-4 py-3 border-t border-border-subtle">
                {(b.site_tasks || []).filter((t) => t.status === "failed").map((t) => (
                  <div key={t.id} className="text-[11px] text-red-600 mb-1">✗ {t.url} — {t.error || "resolve failed"}</div>
                ))}
                {!(b.runs || []).length ? (
                  <div className="text-body-xs text-text-tertiary">Resolving sites…</div>
                ) : (
                  <table className="w-full">
                    <thead><tr>
                      <th className={thClass}>Website</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Open in</th>
                      <th className={`${thClass} text-right`}>Cost</th>
                    </tr></thead>
                    <tbody>
                      {(b.runs || []).map((r) => (
                        <tr key={r.id} className="border-t border-border-subtle">
                          <td className="px-3 py-2 text-body-xs text-text-primary" title={r.website_id}>{r.website_name || r.website_id || "—"}</td>
                          <td className="px-3 py-2">
                            <StatusPill status={r.status} label={r.run_status ? `${r.status} · ${r.run_status}` : r.status} />
                            {r.error && <div className="text-[11px] text-red-600 mt-1">{r.error}</div>}
                          </td>
                          <td className="px-3 py-2 text-[11px]">
                            <div className="flex flex-col gap-0.5" title={r.session_id || ""}>
                              {r.run_id ? (
                                <button onClick={() => navigate(`/admin/control-tower/staging/${r.run_id}`)}
                                  className="text-accent-purple hover:underline text-left">Control Tower ↗</button>
                              ) : (
                                <button onClick={() => navigate("/admin/control-tower")}
                                  className="text-text-tertiary hover:underline text-left" title="orchestrator run id not resolved yet — opens the list">Control Tower</button>
                              )}
                              {front && r.activity_run_id ? (
                                <a href={`${front}/runs/${r.activity_run_id}`} target="_blank" rel="noreferrer"
                                  className="text-accent-purple hover:underline">Platform activity ↗</a>
                              ) : (
                                <span className="text-text-tertiary">{short(r.session_id)}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-body-xs text-right tabular-nums">{r.cost_usd != null ? `$${r.cost_usd.toFixed(2)}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Badge({ label, ok }) {
  return (
    <span className={`px-2.5 py-1 text-[11px] rounded-lg font-medium ${ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
      {label} {ok ? "●" : "○"}
    </span>
  );
}
